import { trackEvent } from '@fs/zion-analytics'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'
import Cache from '@fs/zion-cache'
import zionDebug from '@fs/zion-debug'
import isEmpty from 'lodash.isempty'
import { requestSLSImageData } from '@fs/zion-sls'
import { errorCollector } from '../../shared/utils/utils'
import { getTaskList, taskCompleted } from '../../shared/services/recordEnhancement/recordEnhancementService'
import { FullNameApp } from '../../shared/services/config'
import { arkToApid, getImageArkUrl } from '../../shared/services/das/dasService'

export const defaultInitialState = {
  tasks: [], // The list of tasks
  currentTask: { subElements: [] }, // The current task
  currentIndex: 0, // The current task index (in relation to the amount of tasks on the image)
  artifactId: '', // artifactId of the current image
  doneCount: 0, // How many tasks have been completed NOT what the current task index is
  goal: -1, // How many more tasks the user wants to complete (0 = infinite)

  loading: false, // When to don't do stuff
}
export const tasksReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_TASKLIST': {
      return {
        ...state,
        tasks: action.value,
        currentTask: action.value[state.currentIndex],
      }
    }
    case 'LOAD_IMAGE': {
      return {
        ...state,
        doneCount: state.doneCount === 0 ? 0 : state.doneCount + 1, // Either the first call, or completing the last task on the previous image
        tasks: action.value.newTaskList,
        currentTask: action.value.newTaskList?.[0],
        currentIndex: 0,
        loading: false,
        artifactId: action.value.artifactId,
      }
    }
    case 'CONTINUE_WITH_NEW_GOAL': {
      const newIndex = state.currentIndex + 1
      return {
        ...state,
        doneCount: 0,
        currentIndex: newIndex,
        currentTask: state.tasks[newIndex],
      }
    }
    case 'GO_TO_PREVIOUS_TASK': {
      const newIndex = state.currentIndex > 0 ? state.currentIndex - 1 : 0
      return {
        ...state,
        doneCount: state.doneCount > 0 ? state.doneCount - 1 : 0,
        currentIndex: newIndex,
        currentTask: state.tasks[newIndex],
      }
    }
    case 'GO_TO_NEXT_TASK_WITHOUT_UPDATING_PROGRESS': {
      const newIndex = (state.currentIndex || 0) + 1
      return {
        ...state,
        currentIndex: newIndex,
        currentTask: state.tasks[newIndex],
      }
    }
    case 'GO_TO_NEXT_TASK': {
      const newIndex = (state.currentIndex || 0) + 1
      return {
        ...state,
        doneCount: (state.doneCount || 0) + 1,
        currentIndex: newIndex,
        currentTask: state.tasks[newIndex],
      }
    }
    // this is different than Go To Next Task because it does not update the done count
    case 'SKIP_TASK': {
      const newIndex = (state.currentIndex || 0) + 1
      return {
        ...state,
        currentIndex: newIndex,
        currentTask: state.tasks[newIndex],
      }
    }
    case 'SET_TASKS_GOAL': {
      return {
        ...state,
        goal: action.value || 0,
      }
    }
    case 'SET_LOADING': {
      return {
        ...state,
        loading: action.value,
      }
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}
export const INFINITE_TASKS_GOAL = 0

const debug = zionDebug('rip:useTasks2') // to turn this on do this in the console: localStorage.debug = 'fs:rip:useTasks'
const cache = new Cache({ storeName: 'RIP_settings' })

/**
 * @typedef GenerateTasksParams
 * @param {Object} data - The raw response data from the server
 *
 * @callback generateTasks
 * @param {GenerateTasksParams} params - Needs to contain at least the raw response data from the server
 * @returns {Array} - the tasks for the current image
 *
 * @typedef CurrentTaskUpdatedParams
 * @property {Object} newTask - The new current task
 * @property {Array} imageHighlights - The highlights for the current image
 *
 * @callback currentTaskUpdated
 * @param {CurrentTaskUpdatedParams} params
 * @returns {void}
 *
 * @typedef {Object} UseNewTasksParams
 * @property {currentTaskUpdated} currentTaskUpdated - Called with the value of the current task in the same update cycle
 * @property {generateTasks} generateTasks - A callback that takes the raw response data from the server and chunks it into tasks
 * @property {Function} onGoalCompleted - Called when the user has completed their tasks
 * @property {Function} setError - Called when an error occurs
 * @property {Object} goalDefault - The default goal for the user
 * @property {String} goalCacheKey - The key to use for caching the users goal preference (if not passed, the goal will not be cached)
 *
 * @param {UseNewTasksParams} params
 */
const useTasks = ({
  currentTaskUpdated,
  generateTasks,
  goalDefault,
  goalCacheKey,
  onGoalCompleted = () => {},
  setError,
  types,
}) => {
  if (!goalDefault) throw new Error('You need to pass in a "goalDefault" to useTasks')
  if (!currentTaskUpdated) throw new Error('You need to pass in a "currentTaskUpdated" to useTasks')
  if (!generateTasks) throw new Error('You need to pass in a "generateTasks" to useTasks')

  const { query, setQuery } = useQueryParams()
  const { action, task: taskType, placeCode, language, surname, debugArk } = query
  const [state, dispatch] = useReducer(tasksReducer, defaultInitialState)
  const shared_dasProxy = useFeatureFlag('shared_dasProxy')
  const debuggerHasLoaded = useRef(false)

  useEffect(() => {
    cache.getItem(goalCacheKey).then((cachedGoal) => {
      dispatch({ type: 'SET_TASKS_GOAL', value: cachedGoal || goalDefault })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * @description Called automatically when the user wants to keep doing tasks, and has also run out of tasks on the current image. It fetches data, and uses a callback to chunk the raw response data into tasks.
   */
  const fetchTaskData = useCallback(async () => {
    const isFirstRequest = !state.tasks?.length
    let data
    const useDebuggingArk = debugArk && !debuggerHasLoaded.current
    if (useDebuggingArk) {
      // This happens when we're debugging by passing in a specific debugArk in the URL
      data = await requestSLSImageData({
        partitionKey: debugArk,
        fsUserAgentChain: FullNameApp,
        includeActions: false,
      })
      // Make the response data look like it came from GIVE
      data = {
        slsImageInfo: data,
        artifactId: await arkToApid(data?.elements?.[0]?.partitionKey),
        artifactUrl: getImageArkUrl(data?.elements?.[0]?.partitionKey, shared_dasProxy.isOn),
      }
      debuggerHasLoaded.current = true
    } else {
      try {
        dispatch({ type: 'SET_LOADING', value: true })
        data = await getTaskList({
          placeCode,
          language,
          isFirstRequest,
          apid: debugArk ? null : state.artifactId, // apid is used to mark the current task done. Passing null for debug sessions means the task won't be marked done, regardless of whether it is assigned to the user
          action,
          taskType,
          surname,
        })
      } catch (err) {
        if (err?.message !== 'NoMoreTasks' && err?.message !== 'TaskExpired' && err?.message !== 'No names on task') {
          errorCollector(err, '', {
            codeId: 669,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
        }
        setError(err.message)
      }
    }

    if (data) {
      const newTaskList = generateTasks({ data })
      setQuery({ autoGenImageArk: newTaskList?.[0]?.partitionKey })
      dispatch({ type: 'LOAD_IMAGE', value: { newTaskList, artifactId: data.artifactId } })
    }
  }, [
    debugArk,
    action,
    generateTasks,
    language,
    placeCode,
    setQuery,
    shared_dasProxy.isOn,
    state.tasks,
    state.artifactId,
    surname,
    taskType,
    setError,
  ])

  // Get the first set of tasks
  const initialRender = useRef(true)
  useEffect(() => {
    if (!isEmpty(types) && initialRender.current) {
      fetchTaskData()
      initialRender.current = false
    }
    // we need this check so that we are sure the sls types has been loaded before generating tasks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types])

  /**
   * @description called when the user wants to go back to the previous task (won't cross image or goal-completion boundaries)
   * */
  const goToPreviousTask = useCallback(() => {
    // Don't allow going back across image boundaries or goal-completion boundaries
    if (state.currentIndex === 0) return
    if (state.doneCount === 0) return

    // We want to pass newState data to the consumer in the same render cycle
    const havingGoneToThePreviousTaskState = tasksReducer(state, { type: 'GO_TO_PREVIOUS_TASK', value: null })
    dispatch({ type: 'GO_TO_PREVIOUS_TASK', value: null })
    currentTaskUpdated({ newTask: havingGoneToThePreviousTaskState.currentTask })

    trackEvent({ event_name: 'click_action', link_name: `Back` })
  }, [currentTaskUpdated, state])

  /**
   * @description called when the user wants to move on to the next task (handles end-of-image and end-of-goal cases)
   * */
  const goToNextTask = useCallback(
    async (skipped) => {
      if (state.goal === -1)
        throw new Error(
          'Set a goal before calling goToNextTask 😅. Pass into useTasks, goalDefault, for a quick and dirty fix. Pass in goalCacheKey to start caching a users goal preference'
        )
      const finalTaskOfGoal = state.doneCount === state.goal - 1
      const finalTaskOfImage = state.currentIndex === state.tasks.length - 1
      if (finalTaskOfGoal) {
        if (skipped && state.goal === 1) {
          if (finalTaskOfImage) {
            await fetchTaskData()
          } else {
            dispatch({ type: 'GO_TO_NEXT_TASK_WITHOUT_UPDATING_PROGRESS', value: null })
            currentTaskUpdated({ newTask: state.tasks[state.currentIndex + 1] })
          }
        } else {
          onGoalCompleted()
        }
      } else if (!finalTaskOfImage) {
        dispatch({ type: 'GO_TO_NEXT_TASK', value: null })
        currentTaskUpdated({ newTask: state.tasks[state.currentIndex + 1] })
      } else if (finalTaskOfImage) {
        await fetchTaskData()
      }
      trackEvent({ event_name: 'click_action', link_name: `Next` })
    },
    [currentTaskUpdated, fetchTaskData, onGoalCompleted, state]
  )

  /**
   * @description Called when a user presses Unsure and wants to skip a task without updating the done count
   * */
  const skipTask = useCallback(async () => {
    const finalTaskOfImage = state.currentIndex === state.tasks.length - 1
    if (!finalTaskOfImage) {
      dispatch({ type: 'SKIP_TASK', value: null })
      currentTaskUpdated({ newTask: state.tasks[state.currentIndex + 1] })
    } else if (finalTaskOfImage) {
      await fetchTaskData()
    }
    trackEvent({ event_name: 'click_action', link_name: `Skip` })
  }, [currentTaskUpdated, fetchTaskData, state])

  /**
   * @description Called from a finished state when the user wants to continue doing tasks with a new goal
   * */
  const continueWithNewGoal = useCallback(
    async (goal) => {
      const finalTaskOfImage = state.currentIndex === state.tasks.length - 1
      dispatch({ type: 'SET_TASKS_GOAL', value: goal })
      if (finalTaskOfImage) {
        await fetchTaskData()
      } else {
        dispatch({ type: 'CONTINUE_WITH_NEW_GOAL', value: null })
        currentTaskUpdated({ newTask: state.tasks[state.currentIndex + 1] })
      }
      if (goalCacheKey && goal !== 0) cache.setItem(goalCacheKey, goal)
    },
    [fetchTaskData, state.currentIndex, state.tasks, goalCacheKey, currentTaskUpdated]
  )

  const updateTaskList = useCallback(({ newTaskList }) => {
    if (!newTaskList) throw new Error('NewTaskList is empty!')
    dispatch({ type: 'UPDATE_TASKLIST', value: newTaskList })
  }, [])

  const getNewImage = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', value: true })

      // Mark the current task as complete to ensure we get a new image
      if (state.artifactId) {
        await taskCompleted(state.artifactId, false, taskType)
      }

      // Now fetch a new task - this will be a truly new image since we completed the previous one
      await fetchTaskData()
    } catch (err) {
      // Ensure loading state is cleared on error
      dispatch({ type: 'SET_LOADING', value: false })

      // If there's an error, fall back to page reload as it's more reliable
      console.error('Error getting new image, falling back to page reload:', err)
      window.location.reload()
    }
  }, [state.artifactId, taskType, fetchTaskData])

  debug('new useTasks render', {
    state,
    action,
    taskType,
    placeCode,
    language,
    surname,
    debugArk,
  })

  return {
    continueWithNewGoal,
    goToNextTask,
    goToPreviousTask,
    skipTask,
    updateTaskList,
    fetchTaskData,
    getNewImage,
    currentIndex: state.currentIndex,
    currentTask: state.currentTask,
    doneCount: state.doneCount,
    goal: state.goal,
    loading: state.loading,
  }
}

export default useTasks
